/**
 * DeliveryExceptionsPage — `/delivery/exceptions`.
 *
 * Lists failed route stops (FAILED state) from service-delivery via
 * GET /v1/delivery/exceptions and allows the operator to:
 *   - REATTEMPT: reset stop to PENDING for re-delivery
 *   - RETURN_TO_SENDER: mark as returned (fires mail-item RETURNED transition)
 *   - ESCALATE: flag for supervisor review
 *
 * Wired to POST /v1/delivery/exceptions/:id/resolve.
 */
import { useState, type JSX } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  type ExceptionItem,
  type ExceptionResolution,
  listExceptions,
  resolveException,
} from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const RESOLUTION_LABELS: Record<ExceptionResolution, string> = {
  REATTEMPT: 'Re-attempt',
  RETURN_TO_SENDER: 'Return to sender',
  ESCALATE: 'Escalate',
};

export function DeliveryExceptionsPage(): JSX.Element {
  const [page, setPage] = useState<number>(1);

  const exceptions = useQuery({
    queryKey: ['delivery', 'exceptions', { page }],
    queryFn: () => listExceptions({ page, pageSize: 20 }),
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    exceptions.isError &&
    exceptions.error instanceof ApiError &&
    (exceptions.error.status === 401 || exceptions.error.status === 403);

  const total = exceptions.data?.total ?? 0;
  const pageSize = exceptions.data?.pageSize ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Delivery Exceptions"
        description="Failed delivery stops requiring follow-up."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Open exceptions"
          value={exceptions.isPending ? '—' : denied ? '—' : String(total)}
          hint={denied ? 'Permission required' : 'Failed stops needing follow-up'}
          icon={AlertTriangle}
          tone={denied ? 'warning' : total > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="Recipient absent"
          value={
            exceptions.isPending || denied
              ? '—'
              : String(
                  (exceptions.data?.items ?? []).filter((i) => i.failureReason === 'RECIPIENT_ABSENT').length,
                )
          }
          hint="RECIPIENT_ABSENT stops"
          tone="warning"
        />
        <KPICard
          title="Address invalid"
          value={
            exceptions.isPending || denied
              ? '—'
              : String(
                  (exceptions.data?.items ?? []).filter((i) => i.failureReason === 'ADDRESS_INVALID').length,
                )
          }
          hint="ADDRESS_INVALID stops"
          tone="warning"
        />
        <KPICard
          title="Inaccessible"
          value={
            exceptions.isPending || denied
              ? '—'
              : String(
                  (exceptions.data?.items ?? []).filter((i) => i.failureReason === 'INACCESSIBLE').length,
                )
          }
          hint="INACCESSIBLE stops"
          tone="warning"
        />
      </div>

      {exceptions.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/listExceptions</code>.
        </DeferredNotice>
      ) : exceptions.isError ? (
        <DeferredNotice title="Service unreachable">
          {(exceptions.error as Error).message}
        </DeferredNotice>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Route</th>
                  <th className="text-left px-4 py-2">Stop</th>
                  <th className="text-left px-4 py-2">Mail item</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">Failed</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(exceptions.data?.items ?? []).map((item) => (
                  <ExceptionRow key={item.stopId} item={item} />
                ))}
              </tbody>
            </table>
            {(exceptions.data?.items.length ?? 0) === 0 ? (
              <p className="p-8 text-center text-sm text-muted">No delivery exceptions.</p>
            ) : null}
          </div>
          <div className="flex items-center justify-between mt-4 text-xs text-muted">
            <span>Page {page} of {totalPages} · {total} exceptions</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ExceptionRow({ item }: { readonly item: ExceptionItem }): JSX.Element {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'err'; message: string } | null>(null);
  const [selectedAction, setSelectedAction] = useState<ExceptionResolution>('REATTEMPT');

  const resolveMutation = useMutation({
    mutationFn: () => resolveException(item.stopId, selectedAction),
    onSuccess: (res) => {
      setOutcome({ kind: 'ok', message: `${RESOLUTION_LABELS[selectedAction]} applied (stop: ${res.stopStatus})` });
      // Delay invalidation so the success message remains visible briefly before
      // the row is removed from the refreshed exceptions list.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['delivery', 'exceptions'] });
      }, 3_000);
    },
    onError: (err) => {
      setOutcome({ kind: 'err', message: err instanceof Error ? err.message : 'Resolution failed' });
    },
  });

  return (
    <tr className="hover:bg-gray-50 align-top">
      <td className="px-4 py-2">
        <p className="font-mono text-xs font-bold text-gray-900">{item.routeCode}</p>
        <StatusBadge status={item.routeStatus} />
      </td>
      <td className="px-4 py-2 text-xs text-muted">#{item.sequence}</td>
      <td className="px-4 py-2 font-mono text-xs text-gray-900">{item.mailItemId ? item.mailItemId.slice(0, 12) + '…' : '—'}</td>
      <td className="px-4 py-2 text-xs text-amber-700">
        {item.failureReason ? item.failureReason.replace(/_/g, ' ') : '—'}
      </td>
      <td className="px-4 py-2 text-xs text-muted">
        {item.completedAt ? new Date(item.completedAt).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-2 text-right">
        {outcome ? (
          <p className={outcome.kind === 'ok' ? 'text-xs text-emerald-700' : 'text-xs text-red-700'}>
            {outcome.message}
          </p>
        ) : (
          <div className="inline-flex items-center gap-2">
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value as ExceptionResolution)}
              aria-label="Resolution action"
              className="text-xs px-2 py-1 border border-border rounded-lg bg-white"
            >
              {(Object.entries(RESOLUTION_LABELS) as [ExceptionResolution, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending}
              data-testid={`resolve-${item.stopId}`}
              className="px-3 py-1.5 text-xs font-semibold bg-nipost-green text-white rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              {resolveMutation.isPending ? '…' : 'Apply'}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
